// Phase 36 Batch R — Global address pickers.
//
// Two paired dropdowns + a postal code input that swap together based
// on the selected country.
//
// Props:
//   value: { country_iso2, region_code, postal_code }
//   onChange(next)  -- called whenever any of the three fields change
//   defaultCountry  -- "US" unless the user has set a default
//
// Uses /api/locations/countries + /api/locations/regions endpoints.
// Both are cached in-memory per component instance.

import { useEffect, useMemo, useState } from "react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronsUpDown, Check } from "lucide-react";
import api from "@/lib/api";

// In-memory cache so each open of the wizard doesn't re-hit the network.
const _cache = { countries: null, regionsByCountry: {} };

export async function fetchCountries() {
  if (_cache.countries) return _cache.countries;
  const r = await api.get("/locations/countries");
  _cache.countries = r.data.countries || [];
  return _cache.countries;
}

export async function fetchRegions(country_iso2) {
  if (!country_iso2) return [];
  if (_cache.regionsByCountry[country_iso2]) return _cache.regionsByCountry[country_iso2];
  const r = await api.get(`/locations/regions?country=${country_iso2}`);
  const regions = r.data.regions || [];
  _cache.regionsByCountry[country_iso2] = regions;
  return regions;
}

// Pick a region label that matches the dropdown's "type" majority (so
// the input is labelled "Province" for Canada / "Department" for
// France / "State" for the US, etc.). When the response is mixed (US
// — mostly States with 5 Territories + 1 Federal District), prefer
// the most common type.
function regionLabelFromTypes(regions) {
  if (!regions || regions.length === 0) return "Region";
  const counts = {};
  for (const r of regions) counts[r.type] = (counts[r.type] || 0) + 1;
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  return top || "Region";
}

export function CountrySelect({ value, onChange, testid = "country-select" }) {
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  useEffect(() => { fetchCountries().then(setCountries); }, []);
  const current = countries.find((c) => c.iso2 === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline" role="combobox"
          data-testid={testid}
          className="h-10 w-full justify-between bg-white font-normal text-slate-700"
        >
          {current ? (
            <span className="flex items-center gap-2">
              <span className="text-base">{current.flag}</span>
              <span>{current.name}</span>
              <span className="text-[10px] text-slate-400">{current.iso2}</span>
            </span>
          ) : (
            <span className="text-slate-400">Select country…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((c) => (
                <CommandItem
                  key={c.iso2}
                  value={`${c.name} ${c.iso2}`}
                  data-testid={`${testid}-opt-${c.iso2}`}
                  onSelect={() => { onChange(c.iso2); setOpen(false); }}
                  className="cursor-pointer"
                >
                  <span className="mr-2 text-base">{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                  <span className="text-[10px] text-slate-400 mr-1">{c.iso2}</span>
                  {value === c.iso2 ? <Check className="h-4 w-4" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function RegionSelect({ country, value, onChange, testid = "region-select" }) {
  const [open, setOpen] = useState(false);
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchRegions(country).then((r) => { if (!cancelled) setRegions(r); });
    return () => { cancelled = true; };
  }, [country]);

  const label = regionLabelFromTypes(regions);
  const current = regions.find((r) => r.code === value);

  if (regions.length === 0 && country) {
    // Tiny countries with zero ISO 3166-2 subdivisions — render a
    // disabled placeholder rather than an empty dropdown.
    return (
      <Input
        disabled
        value="—"
        data-testid={`${testid}-empty`}
        className="h-10 bg-slate-50 text-slate-500"
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline" role="combobox"
          data-testid={testid}
          disabled={!country}
          className="h-10 w-full justify-between bg-white font-normal text-slate-700"
        >
          {current ? (
            <span>{current.name} <span className="text-[10px] text-slate-400">({current.code})</span></span>
          ) : (
            <span className="text-slate-400">Select {label.toLowerCase()}…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {regions.map((r) => (
                <CommandItem
                  key={r.code}
                  value={`${r.name} ${r.code}`}
                  data-testid={`${testid}-opt-${r.code}`}
                  onSelect={() => { onChange(r.code); setOpen(false); }}
                  className="cursor-pointer"
                >
                  <span className="flex-1">{r.name}</span>
                  <span className="text-[10px] text-slate-400 mr-2">{r.code}</span>
                  {value === r.code ? <Check className="h-4 w-4" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function GlobalLocationFields({
  country, region, postal,
  onCountryChange, onRegionChange, onPostalChange,
  className = "",
}) {
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchRegions(country).then((r) => { if (!cancelled) setRegions(r); });
    return () => { cancelled = true; };
  }, [country]);

  const regionLabel = useMemo(() => regionLabelFromTypes(regions), [regions]);
  const postalLabel = country === "US" ? "ZIP Code" : "Postal Code";

  return (
    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-3 ${className}`}>
      <div>
        <Label className="text-xs font-medium text-slate-700">Country</Label>
        <div className="mt-1">
          <CountrySelect value={country} onChange={onCountryChange} testid="wizard-country" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-slate-700">{regionLabel}</Label>
        <div className="mt-1">
          <RegionSelect
            country={country} value={region}
            onChange={onRegionChange}
            testid="wizard-region"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-slate-700">{postalLabel}</Label>
        <Input
          value={postal || ""}
          onChange={(e) => onPostalChange(e.target.value)}
          placeholder={country === "US" ? "33602" : "Postal code"}
          data-testid="wizard-postal"
          className="mt-1 h-10"
        />
      </div>
    </div>
  );
}
